import React from 'react';
import { pricingPlans } from '../mockData';
import { Check, X } from 'lucide-react';

const PricingSection = () => {
  return (
    <section id="planos" className="bg-gradient-to-br from-[#F8F9FA] to-[#F8F9FA] py-20">
      <div className="container mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-16 scroll-reveal">
          <p className="text-sm font-bold text-[#fff301] mb-4 uppercase tracking-wider">
            Planos 100% Adaptados a Cada Um
          </p>
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
            Escolhe o teu plano de <span className="text-[#fff301]">perda de peso</span> e muda o teu estilo de vida
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Planos para resultados reais, adaptados ao teu ritmo, objetivos e à tua saúde!
          </p>
        </div>

        {/* Desktop Pricing Cards */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12 pt-6">
          {pricingPlans.map((plan, index) => (
            <div
              key={plan.id}
              className={`bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 relative scroll-reveal-scale ${
                plan.featured ? 'ring-4 ring-[#fff301] scale-105 z-10' : ''
              }`}
              style={{ transitionDelay: `${index * 0.1}s` }}
            >
              {/* Featured Badge */}
              {plan.featured && (
                <div className="bg-black text-white text-center py-2 text-sm font-bold rounded-t-2xl">
                  ⭐ Mais Procurado
                </div>
              )}
              
              {/* Plan Header */}
              <div className={`text-center py-4 ${plan.featured ? 'bg-black text-[#fff301]' : 'bg-[#fff301] text-black'}`}>
                <h3 className="text-xl font-bold">{plan.name}</h3>
              </div>

              {/* Price */}
              <div className="text-center py-8 border-b border-gray-200">
                <div className="text-5xl font-bold text-black mb-2">{plan.price}</div>
                <p className="text-sm text-gray-600">IVA incluído à taxa em vigor</p>
              </div>

              {/* Features */}
              <div className="p-6">
                <h4 className="font-bold text-black mb-4">O que está incluído?</h4>
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      {feature.included ? (
                        <Check className="w-5 h-5 text-[#fff301] flex-shrink-0 mt-0.5" />
                      ) : (
                        <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      )}
                      <span className={feature.included ? 'text-gray-700' : 'text-gray-400'}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* CTA Button - Using native <a> for Safari compatibility */}
                <a
                  href={plan.stripeLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-full font-semibold py-4 rounded-full transition-all duration-300 hover:scale-105 bg-[#fff301] hover:bg-[#e6dd01] text-black flex items-center justify-center text-center block`}
                >
                  Começar agora!
                </a>

                {plan.badge && (
                  <a
                    href={plan.stripeLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-full py-4 rounded-full text-sm font-semibold mt-4 flex items-center justify-center text-center block ${plan.featured ? 'bg-black text-[#fff301] hover:bg-gray-800' : 'bg-transparent text-black border-2 border-gray-300 hover:bg-gray-100'}`}
                  >
                    {plan.featured ? 'Pague em 3x' : plan.badge}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Mobile Pricing Cards */}
        <div className="md:hidden space-y-8 mb-12">
          {pricingPlans.map((plan) => (
            <div
              key={plan.id}
              className={`bg-white rounded-3xl shadow-xl relative overflow-hidden ${
                plan.featured ? 'ring-4 ring-[#fff301] my-6' : ''
              }`}
            >
              {/* Featured Badge */}
              {plan.featured && (
                <div className="bg-black text-white text-center py-3 text-sm font-bold">
                  ⭐ Mais Procurado
                </div>
              )}
              
              {/* Plan Header */}
              <div className={`text-center py-4 ${plan.featured ? 'bg-black text-[#fff301]' : 'bg-[#fff301] text-black'}`}>
                <h3 className="text-xl font-bold">{plan.name}</h3>
              </div>

              {/* Price */}
              <div className="text-center py-8 border-b border-gray-200">
                <div className="text-5xl font-bold text-black mb-2">{plan.price}</div>
                <p className="text-sm text-gray-600">IVA incluído à taxa em vigor</p>
              </div>

              {/* Features */}
              <div className="p-6">
                <h4 className="font-bold text-black mb-4">O que está incluído?</h4>
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-base">
                      {feature.included ? (
                        <Check className="w-6 h-6 text-[#fff301] flex-shrink-0 mt-0.5" />
                      ) : (
                        <X className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                      )}
                      <span className={feature.included ? 'text-gray-700' : 'text-gray-400'}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* CTA Button - Using native <a> for Safari compatibility */}
                <a
                  href={plan.stripeLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-full font-semibold py-4 rounded-full transition-all duration-300 active:scale-95 bg-[#fff301] hover:bg-[#e6dd01] text-black flex items-center justify-center text-center block touch-manipulation`}
                >
                  Começar agora!
                </a>

                {plan.badge && (
                  <a
                    href={plan.stripeLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-full py-4 rounded-full text-sm font-semibold mt-4 flex items-center justify-center text-center block touch-manipulation ${plan.featured ? 'bg-black text-[#fff301] hover:bg-gray-800' : 'bg-transparent text-black border-2 border-gray-300 hover:bg-gray-100'}`}
                  >
                    {plan.featured ? 'Pague em 3x' : plan.badge}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
